<script lang="ts">
  import { onMount } from "svelte"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { registerNavigationGuard } from "@kontextwerk/web-sdk/core/routing/navigation"
  import { mdiContentSave, mdiRefresh } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import LoadingWrapper from "@kontextwerk/web-sdk/ui/layout/LoadingWrapper.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import type { OrgaViewFrameContext } from "../../../../context"
  import { getAdminApi } from "../../../../../../api/kehrblatt/client"
  import {
    fetchUserSettings,
    fetchOrganizationUserSettingsProjection,
    type DailyBriefingPreferences,
  } from "../../../../../../api/kehrblatt/user-settings"
  import {
    fetchDailyBriefingSettings,
    type DailyBriefingSettings,
  } from "../../../../../../api/kehrblatt/daily-briefing"
  import OrganizationSettings from "./_modules/organization-settings"
  import RecipientSettings from "./_modules/recipient-settings"

  const { viewFrameContext } = $props<{ viewFrameContext: OrgaViewFrameContext }>()
  const organizationId = $derived(
    viewFrameContext.organization?.id ?? viewFrameContext.organizationId ?? "",
  )
  const ownUserId = $derived(viewFrameContext.currentUserId ?? "")
  const canManageOrganization = $derived(
    viewFrameContext.permissions.allows("core.org_settings", "manage"),
  )
  const canManageUsers = $derived(viewFrameContext.permissions.allows("core.users", "manage"))
  let settings = $state<DailyBriefingSettings | null>(null)
  let recipients = $state<
    Array<{
      id: string
      label: string
      email: string
      role: string
      preferences: DailyBriefingPreferences
    }>
  >([])
  let loading = $state(true)
  let error = $state<string | null>(null)
  let refresh = $state(0)
  let generation = 0
  type SettingsEditor = { saveChanges: () => Promise<boolean> }
  let organizationEditor = $state<SettingsEditor | undefined>()
  let recipientEditors = $state<Record<string, SettingsEditor | undefined>>({})
  let saving = $state(false)
  let saveError = $state("")
  let saveMessage = $state("")
  let formStates = $state<Record<string, { dirty: boolean; busy: boolean; valid?: boolean }>>({})
  const hasChanges = $derived(Object.values(formStates).some((state) => state.dirty))
  const busy = $derived(saving || Object.values(formStates).some((state) => state.busy))
  const invalidDraft = $derived(
    Object.values(formStates).some((state) => state.dirty && state.valid === false),
  )
  const organizationUnsettled = $derived(
    Boolean(formStates.organization?.dirty || formStates.organization?.busy),
  )
  onMount(() => {
    let active = true
    const unregister = registerNavigationGuard(
      "daily-briefing-settings",
      (_to, _options, resume) => {
        if (busy) return false

        if (!hasChanges) return true

        const targetOrganization = organizationId
        void newApproval({
          title: "Ungespeicherte Einstellungen",
          description: "Möchten Sie die Seite verlassen und Ihre Änderungen verwerfen?",
          approvalLabel: "Verwerfen",
          declineLabel: "Weiter bearbeiten",
          approvalCallback: () => {
            if (active && !busy && organizationId === targetOrganization) {
              formStates = {}
              resume?.()
            }
          },
        })

        return false
      },
    )
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (hasChanges || busy) {
        event.preventDefault()
        event.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", beforeUnload)

    return () => {
      active = false
      unregister()
      window.removeEventListener("beforeunload", beforeUnload)
    }
  })
  const defaults = (): DailyBriefingPreferences => ({
    enabled: true,
    include_pdf: true,
    include_operations: null,
  })

  $effect(() => {
    const org = organizationId
    const own = ownUserId
    const ownAccount = viewFrameContext.organization?.users?.find((user) => user.id === own)
    const manageUsers = canManageUsers
    const role = viewFrameContext.currentUserRole ?? "customer"
    refresh
    const requestId = ++generation
    const controller = new AbortController()
    settings = null
    recipients = []
    formStates = {}
    saving = false
    saveError = saveMessage = ""
    loading = true
    error = null
    const load = async () => {
      try {
        const configuration = await fetchDailyBriefingSettings(org, controller.signal)

        if (requestId !== generation) return

        let rows: typeof recipients = []

        if (manageUsers) {
          const preferences = await fetchOrganizationUserSettingsProjection(org)
          const byUser = new Map(
            preferences.items.map((item) => [item.user_id, item.settings.daily_briefing]),
          )
          let offset = 0
          while (true) {
            const page = await getAdminApi().users.listForOrg(org, { limit: 200, offset })
            const users = page.items ?? []
            rows.push(
              ...users
                .filter((user) => user.is_active)
                .map((user) => ({
                  id: user.id,
                  label:
                    user.username ||
                    [user.first_name, user.last_name].filter(Boolean).join(" ") ||
                    user.email,
                  email: user.email,
                  role: user.role,
                  preferences: byUser.get(user.id) ?? defaults(),
                })),
            )
            offset += users.length

            if (!users.length || offset >= page.total) break

            if (requestId !== generation) return
          }
        } else if (own) {
          const personal = await fetchUserSettings(own)
          rows = [
            {
              id: own,
              label:
                ownAccount?.username ||
                [ownAccount?.first_name, ownAccount?.last_name].filter(Boolean).join(" ") ||
                "Meine Morgenübersicht",
              email: ownAccount?.email ?? "",
              role,
              preferences: personal.daily_briefing ?? defaults(),
            },
          ]
        }

        if (requestId !== generation) return

        settings = configuration
        recipients = rows
      } catch {
        if (requestId === generation) error = "Die Einstellungen konnten nicht geladen werden."
      } finally {
        if (requestId === generation) loading = false
      }
    }

    if (org && own) void load()

    return () => {
      generation++
      controller.abort()
    }
  })

  /** Save the page's changed editors and keep failed drafts available for retry. */
  async function saveAllSettings() {
    if (loading || busy || !hasChanges || invalidDraft) return

    const requestGeneration = generation
    const editors = [
      organizationEditor,
      ...recipients.map((recipient) => recipientEditors[recipient.id]),
    ].filter((editor): editor is SettingsEditor => Boolean(editor))
    saving = true
    saveError = saveMessage = ""
    try {
      let allSaved = true
      for (const editor of editors) {
        const saved = await editor.saveChanges()

        if (requestGeneration !== generation) return

        allSaved = saved && allSaved
      }

      if (allSaved) {
        saveMessage = "Alle Änderungen gespeichert."
      } else {
        saveError =
          "Nicht alle Einstellungen konnten gespeichert werden. Bitte versuchen Sie es für die verbleibenden Änderungen erneut."
      }
    } finally {
      if (requestGeneration === generation) saving = false
    }
  }
</script>

<div class="daily-briefing">
  {#if error}
    <div class="state-block">
      <p role="alert">{error}</p>
      <CTA
        type="secondaryButton"
        content="Erneut versuchen"
        icon={mdiRefresh}
        darkMode={false}
        onClick={() => refresh++}
      />
    </div>
  {:else if loading || !settings}
    <div role="status" aria-label="Morgenübersicht wird geladen">
      <LoadingWrapper loading={true} progressLabel="Morgenübersicht wird geladen …" />
    </div>
  {:else}
    {#key organizationId}
      <OrganizationSettings
        bind:this={organizationEditor}
        {organizationId}
        {settings}
        canManage={canManageOrganization}
        pageSaving={saving}
        onSaved={(value) => (settings = value)}
        onStateChange={(state) => {
          formStates.organization = state
        }}
      />
      <section class="recipients" aria-label="Empfänger">
        <SectionTitle
          title="Empfänger"
          description="Je Person festlegen, welche Inhalte in der Morgenübersicht erscheinen."
          brightBackground={true}
          small={true}
        />
        {#each recipients as recipient (recipient.id)}
          <RecipientSettings
            bind:this={recipientEditors[recipient.id]}
            {organizationId}
            {recipient}
            timezone={settings.timezone}
            canSend={recipient.id === ownUserId || canManageUsers}
            isSelf={recipient.id === ownUserId}
            {organizationUnsettled}
            pageSaving={saving}
            onStateChange={(state) => {
              formStates[recipient.id] = state
            }}
          />
        {/each}
      </section>
      <div class="page-save-actions">
        {#if saveError}<p role="alert">{saveError}</p>{/if}
        {#if saveMessage && !hasChanges}<p role="status">{saveMessage}</p>{/if}
        <CTA
          type="primaryButton"
          content={saving ? "Wird gespeichert …" : "Speichern"}
          icon={mdiContentSave}
          darkMode={false}
          disabled={busy || !hasChanges || invalidDraft}
          onClick={saveAllSettings}
        />
      </div>
    {/key}
  {/if}
</div>

<style>
  .daily-briefing {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 2rem;
    min-width: 0;
  }
  .state-block p {
    margin: 0;
    color: var(--text-invers-100);
    line-height: 1.5;
  }
  .state-block {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  .recipients {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
    min-width: 0;
  }
  .page-save-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  .page-save-actions p {
    margin: 0;
    color: var(--text-invers-100);
  }
</style>
